import { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

// I3 — "add to home screen" affordance for the client portal.
//  - Android/Chrome: capture `beforeinstallprompt` and offer a one-tap install.
//  - iOS Safari (no prompt API): show a short manual hint.
//  - Never shown when already installed (standalone) or after the user dismisses.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwa-install-dismissed';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS Safari exposes navigator.standalone when launched from the home screen.
  (window.navigator as unknown as { standalone?: boolean }).standalone === true;

const ua = () => window.navigator.userAgent;
const isIos = () => /iphone|ipad|ipod/i.test(ua());
// iOS Safari only (Chrome/Firefox on iOS can't A2HS the same way).
const isIosSafari = () => isIos() && /safari/i.test(ua()) && !/crios|fxios|edgios/i.test(ua());

const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHint, setIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault(); // stop Chrome's default mini-infobar; we show our own
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS never fires beforeinstallprompt → offer the manual instructions.
    if (isIosSafari()) {
      setIosHint(true);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 inset-x-4 z-50 mx-auto max-w-md rounded-lg bg-white shadow-lg border border-gray-200 p-4 flex items-start gap-3">
      <div className="rounded-md bg-green-50 p-2 text-green-700">
        {iosHint ? <Share size={20} /> : <Download size={20} />}
      </div>
      <div className="flex-1 text-sm">
        <div className="font-semibold text-gray-800">Instala la app en tu móvil</div>
        {iosHint ? (
          <p className="text-gray-600 mt-1">
            Pulsa <strong>Compartir</strong> <Share size={13} className="inline align-text-bottom" /> y
            luego <strong>“Añadir a pantalla de inicio”</strong>.
          </p>
        ) : (
          <p className="text-gray-600 mt-1">Acceso directo desde tu pantalla de inicio, como una app.</p>
        )}
        {!iosHint && (
          <button
            onClick={install}
            className="mt-2 inline-flex items-center gap-1 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1.5"
          >
            <Download size={15} /> Instalar app
          </button>
        )}
      </div>
      <button onClick={dismiss} className="text-gray-400 hover:text-gray-600" aria-label="Cerrar">
        <X size={18} />
      </button>
    </div>
  );
};

export default PWAInstallPrompt;
